/**
 * Phone Number Utilities
 * Phone number validation and formatting
 */

export class PhoneUtils {
  /**
   * Validate US NPI number
   * @param {string} npi - NPI number
   * @returns {boolean}
   */
  static validateUSNPI(npi) {
    if (!/^\d{10}$/.test(npi)) {
      return false;
    }

    const digits = npi.split('').map(Number);
    const checkDigit = digits.pop();
    let sum = 24; // Special NPI prefix value

    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = digits[i];
      if ((digits.length - 1 - i) % 2 === 0) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      sum += digit;
    }

    const calculatedCheckDigit = (10 - (sum % 10)) % 10;
    return checkDigit === calculatedCheckDigit;
  }

  /**
   * Validate Israeli ID number
   * @param {string} id - Israeli ID number
   * @returns {boolean}
   */
  static validateIsraeliID(id) {
    const idStr = String(id).trim();
    if (idStr.length > 9 || idStr.length < 5 || isNaN(idStr)) {
      return false;
    }

    const paddedId = idStr.length < 9 ? ('00000000' + idStr).slice(-9) : idStr;
    
    return (
      Array.from(paddedId, Number).reduce((counter, digit, i) => {
        const step = digit * ((i % 2) + 1);
        return counter + (step > 9 ? step - 9 : step);
      }, 0) % 10 === 0
    );
  }

  /**
   * Validate Israeli phone number
   * @param {string} phone - Phone number
   * @returns {boolean}
   */
  static validateIsraeliPhone(phone) {
    const cleaned = phone.replace(/\D/g, '');
    const mobileRegex = /^05\d{8}$/;
    return mobileRegex.test(cleaned);
  }

  /**
   * Clean phone number (remove non-digits)
   * @param {string} phone - Phone number
   * @returns {string}
   */
  static cleanPhone(phone) {
    return String(phone || '').replace(/\D/g, '');
  }

  /**
   * Format phone number for display
   * @param {string} phone - Phone number
   * @param {string} countryCode - Country code
   * @returns {string}
   */
  static formatPhone(phone, countryCode = '') {
    const cleaned = this.cleanPhone(phone);
    if (!cleaned) return '';
    
    if (countryCode) {
      return `${countryCode} ${cleaned}`;
    }
    
    return cleaned;
  }
}

